const immersiveVideoScrollParts = new Set();

function updateImmersiveState(instance, isActive) {
  if (isActive && instance.hideQuickAdd) immersiveVideoScrollParts.add(instance);
  else immersiveVideoScrollParts.delete(instance);
  document.documentElement.classList.toggle('vsp-immersive-active', immersiveVideoScrollParts.size > 0);
}

class VideoScrollParts {
  constructor(section) {
    this.section = section;
    this.desktopVideo = section.querySelector('.vsp__video--desktop');
    this.mobileVideo = section.querySelector('.vsp__video--mobile');
    this.video = null;
    this.videos = Array.from(section.querySelectorAll('video'));
    this.media = section.querySelector('[data-vsp-media]');
    this.finishAt = Number(section.dataset.videoFinish) || 0.75;
    this.hideQuickAdd = section.dataset.hideQuickAdd === 'true';
    this.targetTime = 0;
    this.renderedTime = -1;
    this.scrollProgress = 0;
    this.rafId = null;
    this.lastSeekAt = 0;
    this.isNearViewport = false;
    this.revealWhenReady = false;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const lowPowerDevice = Boolean(
      connection?.saveData ||
      (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
      (navigator.deviceMemory && navigator.deviceMemory <= 4)
    );
    this.seekInterval = 1000 / (lowPowerDevice ? 24 : 30);

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.render = this.render.bind(this);

    this.initVisibilityObserver();
    this.initLoadObserver();
    this.updateLines();
    this.syncMediaSize();
    window.addEventListener('resize', this.onResize, { passive: true });
    window.visualViewport?.addEventListener('resize', this.onResize, { passive: true });

    const header = document.querySelector('.shopify-section--header');
    if (header && 'ResizeObserver' in window) {
      this.headerResizeObserver = new ResizeObserver(this.onResize);
      this.headerResizeObserver.observe(header);
    }

    this.videos.forEach((video) => {
      video.pause();
      video.muted = true;
      video.playsInline = true;
      video.addEventListener('loadedmetadata', () => {
        if (video === this.video) this.onReady();
      });
    });

    this.selectVideo();
  }

  selectVideo() {
    const useMobile = window.matchMedia('(max-width: 749px)').matches;
    const nextVideo = useMobile && this.mobileVideo ? this.mobileVideo : this.desktopVideo;
    if (!nextVideo) return;

    if (nextVideo !== this.video) {
      this.video?.pause();
      this.video = nextVideo;
      this.ready = false;
      this.renderedTime = -1;
      this.lastSeekAt = 0;
      this.section.classList.remove('is-ready');
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.isNearViewport) this.loadVideo(this.video);
  }

  loadVideo(video) {
    if (!video) return;

    if (video.dataset.sourceHydrated === 'true') {
      if (video.readyState >= 1) this.onReady();
      return;
    }

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.dataset.sourceHydrated = 'true';
    video.preload = 'auto';
    video.load();
  }

  onReady() {
    if (!this.video || this.ready || !Number.isFinite(this.video.duration)) return;
    this.ready = true;
    this.section.classList.add('is-ready');
    this.syncMediaSize();
    if (!this.scrollListening) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.scrollListening = true;
    }
    this.onScroll();

    if (this.revealWhenReady) {
      this.revealWhenReady = false;
      this.showEndState();
    }
  }

  onScroll() {
    const rect = this.section.getBoundingClientRect();
    const distance = Math.max(1, rect.height - window.innerHeight);
    this.scrollProgress = Math.min(1, Math.max(0, -rect.top / distance));

    const videoProgress = Math.min(1, this.scrollProgress / this.finishAt);
    this.targetTime = videoProgress * Math.max(0, this.video.duration - 0.001);

    const annotationsVisible = this.scrollProgress >= this.finishAt + 0.015;
    this.section.classList.toggle('is-annotated', annotationsVisible);

    if (!this.rafId) this.rafId = requestAnimationFrame(this.render);
  }

  render(timestamp) {
    this.rafId = null;
    if (!this.ready) return;

    if (this.lastSeekAt && timestamp - this.lastSeekAt < this.seekInterval) {
      this.rafId = requestAnimationFrame(this.render);
      return;
    }
    this.lastSeekAt = timestamp;

    const difference = this.targetTime - (this.renderedTime < 0 ? this.video.currentTime : this.renderedTime);
    const nextTime = Math.abs(difference) < 0.015
      ? this.targetTime
      : (this.renderedTime < 0 ? this.video.currentTime : this.renderedTime) + difference * 0.22;

    if (Math.abs(nextTime - this.video.currentTime) > 0.008) {
      try {
        this.video.currentTime = nextTime;
      } catch (error) {
        // The browser may briefly reject seeking while video metadata is updating.
      }
    }
    this.renderedTime = nextTime;

    if (Math.abs(this.targetTime - this.renderedTime) > 0.015) {
      this.rafId = requestAnimationFrame(this.render);
    }
  }

  updateLines() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    this.section.querySelectorAll('[data-vsp-callout]').forEach((callout) => {
      const style = getComputedStyle(callout);
      const pointX = parseFloat(style.getPropertyValue(isMobile ? '--vsp-mobile-point-x' : '--vsp-point-x'));
      const pointY = parseFloat(style.getPropertyValue(isMobile ? '--vsp-mobile-point-y' : '--vsp-point-y'));
      const labelX = parseFloat(style.getPropertyValue(isMobile ? '--vsp-mobile-label-x' : '--vsp-label-x'));
      const labelY = parseFloat(style.getPropertyValue(isMobile ? '--vsp-mobile-label-y' : '--vsp-label-y'));
      const line = this.section.querySelector(`#${CSS.escape(callout.dataset.lineId)}`);

      if (!line || [pointX, pointY, labelX, labelY].some(Number.isNaN)) return;
      const elbowX = pointX + (labelX - pointX) * 0.72;
      line.setAttribute('points', `${pointX},${pointY} ${elbowX},${labelY} ${labelX},${labelY}`);
    });
  }

  syncMediaSize() {
    if (!this.media) return;
    const sticky = this.section.querySelector('.vsp__sticky');
    if (!sticky) return;

    const stickyStyle = getComputedStyle(sticky);
    const availableHeight = Math.max(
      1,
      sticky.clientHeight - parseFloat(stickyStyle.paddingTop) - parseFloat(stickyStyle.paddingBottom)
    );
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const sectionStyle = getComputedStyle(this.section);
    const configuredRatio = parseFloat(sectionStyle.getPropertyValue(
      mobile ? '--vsp-mobile-media-ratio' : '--vsp-media-ratio'
    ));
    const videoRatio = this.video?.videoWidth && this.video?.videoHeight
      ? this.video.videoWidth / this.video.videoHeight
      : configuredRatio;
    const mediaWidth = Math.min(this.section.clientWidth, availableHeight * (videoRatio || 1.777777));
    this.media.style.setProperty('--vsp-computed-media-width', `${Math.max(1, mediaWidth)}px`);
  }

  initVisibilityObserver() {
    this.visibilityObserver = new IntersectionObserver((entries) => {
      updateImmersiveState(this, entries[0]?.isIntersecting === true);
    }, { threshold: 0 });
    this.visibilityObserver.observe(this.section);
  }

  initLoadObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isNearViewport = true;
      return;
    }

    this.loadObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting !== true) return;

      this.isNearViewport = true;
      this.selectVideo();
      this.loadObserver.disconnect();
    }, { rootMargin: '1200px 0px', threshold: 0 });
    this.loadObserver.observe(this.section);
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.selectVideo();
      this.syncMediaSize();
      this.updateLines();
      if (this.ready) this.onScroll();
    }, 100);
  }

  showForEditor() {
    this.isNearViewport = true;
    this.selectVideo();

    if (!this.video || !this.ready) {
      this.revealWhenReady = true;
      return;
    }

    this.showEndState();
  }

  showEndState() {
    this.targetTime = Math.max(0, this.video.duration - 0.001);
    this.video.currentTime = this.targetTime;
    this.renderedTime = this.targetTime;
    this.section.classList.add('is-annotated');
  }

  destroy() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    window.visualViewport?.removeEventListener('resize', this.onResize);
    this.headerResizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.loadObserver?.disconnect();
    updateImmersiveState(this, false);
    clearTimeout(this.resizeTimer);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }
}

const videoScrollPartsInstances = new Map();

function initVideoScrollParts(root = document) {
  root.querySelectorAll('[data-video-scroll-parts]').forEach((section) => {
    if (videoScrollPartsInstances.has(section)) return;
    videoScrollPartsInstances.set(section, new VideoScrollParts(section));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initVideoScrollParts());
} else {
  initVideoScrollParts();
}

document.addEventListener('shopify:section:load', (event) => initVideoScrollParts(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-video-scroll-parts]').forEach((section) => {
    videoScrollPartsInstances.get(section)?.destroy();
    videoScrollPartsInstances.delete(section);
  });
});

document.addEventListener('shopify:block:select', (event) => {
  const section = event.target.closest('[data-video-scroll-parts]');
  videoScrollPartsInstances.get(section)?.showForEditor();
});
